<template>
  <section v-if="panel" class="chat-panel" :aria-labelledby="`${uid}-title`" :aria-busy="submitting">
    <header class="panel-header">
      <span class="panel-kind">{{ kindLabel }}</span>
      <h3 :id="`${uid}-title`">{{ panel.title }}</h3>
      <p v-if="panel.description">{{ panel.description }}</p>
    </header>

    <div v-if="panel.type === 'cards'" class="panel-cards">
      <article v-for="(item, index) in panel.items" :key="item.id" class="panel-card">
        <span class="card-number">{{ String(index + 1).padStart(2, '0') }}</span>
        <h4>{{ item.title }}</h4><p>{{ item.body }}</p>
        <details v-if="item.detail"><summary>展开说明</summary><p>{{ item.detail }}</p></details>
        <button type="button" :disabled="busy" @click="choose(item.id)">沿着这个方向聊 →</button>
      </article>
    </div>

    <form v-else-if="panel.type === 'choices'" @submit.prevent="submit">
      <fieldset :disabled="busy" :aria-describedby="error ? `${uid}-error` : undefined">
        <legend>{{ panel.multiple ? '可以选择多项' : '选择一项' }}</legend>
        <label v-for="item in panel.options" :key="item.id" class="choice-option" :class="{ selected: state.selected?.includes(item.id) }">
          <input :type="panel.multiple ? 'checkbox' : 'radio'" :name="uid" :value="item.id" :checked="state.selected?.includes(item.id)" @change="toggleChoice(item.id, $event.target.checked)" />
          <span><strong>{{ item.label }}</strong><small v-if="item.description">{{ item.description }}</small></span>
        </label>
      </fieldset>
      <button class="primary" type="submit" :disabled="busy">选好了，继续聊</button>
    </form>

    <form v-else-if="panel.type === 'form'" @submit.prevent="submit">
      <div class="form-fields">
        <label v-for="field in panel.fields" :key="field.id" :class="{ wide: field.type === 'textarea' }">
          <span>{{ field.label }} <small v-if="field.required">（必填）</small></span>
          <textarea v-if="field.type === 'textarea'" v-model="state.values[field.id]" :required="field.required" :disabled="busy" maxlength="3000" rows="3" @input="scheduleSave" />
          <select v-else-if="field.type === 'select'" v-model="state.values[field.id]" :required="field.required" :disabled="busy" @change="scheduleSave">
            <option value="">请选择</option><option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
          </select>
          <input v-else v-model="state.values[field.id]" :type="field.type" :required="field.required" :disabled="busy" maxlength="3000" @input="scheduleSave" />
        </label>
      </div>
      <button class="primary" type="submit" :disabled="busy">填写好了，帮我整理</button>
    </form>

    <div v-else-if="panel.type === 'comparison'">
      <label class="inline-control"><input type="checkbox" v-model="differencesOnly" />只看差异</label>
      <div class="table-scroll" tabindex="0" aria-label="方案对比，可横向滚动">
        <table><caption class="sr-only">{{ panel.title }}</caption>
          <thead><tr><th scope="col">比较维度</th><th v-for="column in panel.columns" :key="column.id" scope="col">{{ column.label }}</th></tr></thead>
          <tbody><tr v-for="row in comparisonRows" :key="row.label"><th scope="row">{{ row.label }}</th><td v-for="(value, index) in row.values" :key="index">{{ value }}</td></tr></tbody>
        </table>
        <p v-if="!comparisonRows.length" class="quiet">这些维度没有明显差异。</p>
      </div>
      <div class="panel-actions"><button v-for="column in panel.columns" :key="column.id" :disabled="busy" type="button" @click="choose(column.id)">继续聊{{ column.label }}</button></div>
    </div>

    <div v-else-if="panel.type === 'checklist'">
      <div class="progress-label"><span>已完成 {{ state.checked.length }} / {{ panel.items.length }}</span><label class="inline-control"><input type="checkbox" v-model="todoOnly" />只看待办</label></div>
      <progress :value="state.checked.length" :max="panel.items.length" aria-label="任务完成进度" />
      <ul class="task-list"><li v-for="item in visibleTasks" :key="item.id">
        <label :class="{ checked: state.checked.includes(item.id) }"><input v-model="state.checked" type="checkbox" :value="item.id" :disabled="busy" @change="scheduleSave" /><span><strong>{{ item.label }}</strong><small v-if="item.detail">{{ item.detail }}</small></span></label>
      </li></ul>
      <p v-if="!visibleTasks.length" class="quiet">都完成了，可以回顾一下实际效果。</p>
      <button type="button" :disabled="busy" @click="submit">按完成情况继续聊</button>
    </div>

    <div v-else-if="panel.type === 'chart'">
      <div class="chart-controls"><label>查看指标 <select v-model="metric"><option v-for="series in panel.series" :key="series.id" :value="series.id">{{ series.label }}{{ series.unit ? `（${series.unit}）` : '' }}</option></select></label><button type="button" :aria-pressed="tableView" @click="tableView = !tableView">{{ tableView ? '查看图表' : '查看数据表' }}</button></div>
      <div v-if="!tableView" class="chart" :aria-label="`${activeSeries.label}，单位${activeSeries.unit || '无'}`">
        <div v-for="(value, index) in activeSeries.values" :key="panel.labels[index]" class="bar-column">
          <button type="button" class="bar-button" :aria-label="`${panel.labels[index]}：${value}${activeSeries.unit}`" :aria-pressed="selectedPoint === index" @click="selectedPoint = index">
            <span class="bar-value">{{ value }}</span><span class="bar" :style="{ height: `${barHeight(value)}px` }"></span>
          </button><span class="bar-label">{{ panel.labels[index] }}</span>
        </div>
      </div>
      <div v-else class="table-scroll" tabindex="0" aria-label="原始数据，可横向滚动"><table><caption class="sr-only">{{ panel.title }}原始数据</caption><thead><tr><th scope="col">记录</th><th v-for="series in panel.series" :key="series.id" scope="col">{{ series.label }}（{{ series.unit || '数值' }}）</th></tr></thead><tbody><tr v-for="(label, index) in panel.labels" :key="label"><th scope="row">{{ label }}</th><td v-for="series in panel.series" :key="series.id">{{ series.values[index] }}</td></tr></tbody></table></div>
      <p class="chart-source" aria-live="polite">{{ selectedPoint === null ? '数据来自你提供的记录。点击柱子查看对应原文。' : `对应原文：${activeSeries.sources[selectedPoint]}` }}</p>
    </div>

    <footer v-if="panel.type !== 'chart'" class="panel-footer">
      <span role="status">{{ submitting ? '正在提交…' : saveStatus }}</span>
      <button v-if="saveFailed" type="button" @click="save">重试保存</button>
    </footer>
    <p v-if="error" :id="`${uid}-error`" class="panel-error" role="alert">{{ error }}</p>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { apiService } from '../utils/api';

const props = defineProps({ ui: { type: Object, required: true }, savedState: Object, messageId: { type: String, required: true }, disabled: Boolean });
const emit = defineEmits(['submit', 'state']);
const panel = computed(() => props.ui?.version === 1 ? props.ui.panel : null);
const uid = computed(() => `chat-panel-${props.messageId}`);
const kindLabel = computed(() => ({ cards: '几个方向，选一个继续', choices: '按你的情况选择', form: '把实际情况记下来', comparison: '放在一起看，更好决定', checklist: '一步一步完成', chart: '从记录里看变化' }[panel.value?.type] || ''));
const initialState = () => JSON.parse(JSON.stringify(props.savedState || (panel.value?.type === 'form' ? { values: Object.fromEntries(panel.value.fields.map((field) => [field.id, ''])) } : panel.value?.type === 'checklist' ? { checked: [] } : { selected: [] })));
const state = ref(initialState());
const error = ref('');
const saveStatus = ref('');
const saveFailed = ref(false);
const submitting = ref(false);
const busy = computed(() => props.disabled || submitting.value);
const differencesOnly = ref(false);
const todoOnly = ref(false);
const metric = ref(panel.value?.series?.[0]?.id);
const tableView = ref(false);
const selectedPoint = ref(null);
const activeSeries = computed(() => panel.value?.series?.find((series) => series.id === metric.value) || panel.value?.series?.[0]);
const comparisonRows = computed(() => panel.value?.rows?.filter((row) => !differencesOnly.value || new Set(row.values).size > 1) || []);
const visibleTasks = computed(() => panel.value?.items?.filter((item) => !todoOnly.value || !state.value.checked?.includes(item.id)) || []);
const barHeight = (value) => Math.abs(value) / Math.max(1, ...activeSeries.value.values.map(Math.abs)) * 138;
let timer;
let saveQueue = Promise.resolve();
let alive = true;

watch(metric, () => { selectedPoint.value = null; });

const persist = (snapshot, submit = false) => {
  const id = props.messageId;
  const task = saveQueue.catch(() => {}).then(() => apiService.saveUiState(id, snapshot, submit));
  saveQueue = task;
  return task;
};

const save = async () => {
  clearTimeout(timer);
  const snapshot = JSON.parse(JSON.stringify(state.value));
  saveStatus.value = '正在保存…';
  try {
    const result = await persist(snapshot);
    if (alive) {
      emit('state', result.state);
      saveStatus.value = '已保存';
      saveFailed.value = false;
      error.value = '';
    }
  } catch (cause) {
    console.error('组件状态保存失败', cause);
    if (alive) {
      error.value = `尚未保存：${cause.message || '请稍后重试'}`;
      saveStatus.value = '尚未保存';
      saveFailed.value = true;
    }
  }
};

const scheduleSave = () => {
  clearTimeout(timer);
  saveStatus.value = '待保存';
  timer = setTimeout(save, 650);
};

const toggleChoice = (id, checked) => {
  state.value.selected = panel.value.multiple
    ? checked ? [...(state.value.selected || []), id] : state.value.selected.filter((value) => value !== id)
    : [id];
  scheduleSave();
};

const submit = async () => {
  if (busy.value) return;
  clearTimeout(timer);
  submitting.value = true;
  error.value = '';
  try {
    const result = await persist(JSON.parse(JSON.stringify(state.value)), true);
    if (!alive) return;
    emit('state', result.state);
    saveStatus.value = '已保存';
    saveFailed.value = false;
    if (props.disabled) { error.value = '当前正在回复，内容已保存，稍后可以继续提交。'; return; }
    emit('submit', { messageId: props.messageId, state: result.state, content: result.content });
  } catch (cause) {
    console.error('组件提交失败', cause);
    error.value = cause.message || '提交失败，请重试';
  } finally { submitting.value = false; }
};

const choose = (id) => { state.value.selected = [id]; submit(); };
onBeforeUnmount(() => {
  if (saveStatus.value === '待保存') save();
  alive = false;
  clearTimeout(timer);
});
</script>

<style scoped lang="scss">
.chat-panel { margin-top: 22px; padding: 24px; background: #f8faff; border: 1px solid #cddcf0; border-radius: 16px; color: #24374e; font-size: 15px; line-height: 1.65; overflow-wrap: anywhere; }
.panel-header { margin-bottom: 22px; h3 { font-size: 21px; margin: 8px 0; color: #173353; line-height: 1.4; } p { margin: 0; color: #536780; } }
.panel-kind { color: #3263a4; font-size: 12px; font-weight: 600; }
.chat-panel button, .chat-panel input, .chat-panel textarea, .chat-panel select { font: inherit; }
.chat-panel button { cursor: pointer; background: white; color: #285b9d; border: 1px solid #b8cde9; border-radius: 8px; padding: 9px 14px; min-height: 40px; white-space: normal; }
.chat-panel button:hover:not(:disabled) { background: #eaf2ff; }
.chat-panel button.primary { background: #2b5fb4; color: white; border-color: #2b5fb4; margin-top: 20px; }
.chat-panel button:disabled { opacity: .55; cursor: wait; }
.chat-panel :is(button, input, select, textarea, summary, [tabindex]):focus-visible { outline: 3px solid #3975cd; outline-offset: 3px; }
.panel-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr)); gap: 14px; }
.panel-card { background: white; border: 1px solid #d5e1ef; border-radius: 12px; padding: 18px; display: flex; flex-direction: column; align-items: flex-start; h4 { font-size: 17px; margin: 6px 0; } p { margin: 0 0 15px; } button { margin-top: auto; } details { margin-bottom: 14px; } summary { cursor: pointer; color: #285b9d; margin-bottom: 8px; } }
.card-number { color: #4473ae; font-size: 13px; font-variant-numeric: tabular-nums; }
fieldset { border: 0; padding: 0; margin: 0; min-width: 0; } legend { color: #5b6e85; font-size: 13px; margin-bottom: 10px; }
.choice-option { display: flex; gap: 12px; align-items: flex-start; background: white; border: 1px solid #d0deef; border-radius: 10px; padding: 14px; margin-top: 10px; cursor: pointer; }
.choice-option.selected { border-color: #4a7abf; background: #edf4ff; }
.chat-panel input:is([type=radio], [type=checkbox]) { accent-color: #2b5fb4; width: 18px; height: 18px; flex-shrink: 0; margin: 4px 0 0; }
.chat-panel small { display: block; color: #536780; font-size: 13px; font-weight: 400; }
.form-fields { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; label { display: flex; flex-direction: column; gap: 7px; } small { display: inline; } .wide { grid-column: 1/-1; } }
.chat-panel input:not([type=checkbox]):not([type=radio]), .chat-panel select, .chat-panel textarea { box-sizing: border-box; width: 100%; min-width: 0; border: 1px solid #b5c8df; border-radius: 8px; background: white; color: #24374e; padding: 10px; }
.chat-panel textarea { resize: vertical; }
.table-scroll { overflow-x: auto; border: 1px solid #d0deef; border-radius: 10px; margin-top: 12px; background: white; }
table { border-collapse: collapse; width: 100%; font-size: 14px; min-width: 440px; } th, td { border-bottom: 1px solid #e0e8f3; padding: 13px; text-align: left; vertical-align: top; } thead th { background: #edf3fc; color: #224f88; } tbody th { min-width: 90px; font-weight: 500; }
.inline-control { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; }
.panel-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 18px; }
.progress-label { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px; }
progress { width: 100%; height: 8px; accent-color: #2b5fb4; margin: 14px 0; }
.task-list { list-style: none; margin: 0 0 20px; padding: 0; li { border-bottom: 1px solid #dce6f3; } label { display: flex; align-items: flex-start; gap: 12px; padding: 15px 0; cursor: pointer; } .checked strong { color: #5b6e85; text-decoration: line-through; } }
.quiet { color: #536780; padding: 10px; }
.chart-controls { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 14px; label { font-size: 13px; } select { margin-top: 5px; } }
.chart { display: flex; align-items: flex-end; gap: 10px; overflow-x: auto; padding: 26px 8px 10px; }
.bar-column { flex: 1; min-width: 48px; text-align: center; font-size: 12px; }
.chat-panel .bar-button { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 4px; height: 170px; min-height: 0; padding: 3px; border: 0; width: 100%; background: transparent; }
.bar { width: min(100%, 44px); background: #4d7fc5; border-radius: 4px 4px 0 0; min-height: 1px; }
.bar-button[aria-pressed=true] .bar { background: #234f90; }
.bar-value { font-size: 12px; font-variant-numeric: tabular-nums; }.bar-label { display: block; margin-top: 8px; }
.chart-source { font-size: 13px; color: #536780; margin-bottom: 0; }
.panel-footer { display: flex; align-items: center; gap: 10px; margin-top: 16px; font-size: 12px; color: #52677f; min-height: 20px; }
.panel-error { border-left: 3px solid #a83f35; color: #96342b; padding-left: 10px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
@media(max-width: 600px) { .chat-panel { padding: 16px; margin-top: 16px; } .form-fields { grid-template-columns: 1fr; } .panel-header h3 { font-size: 19px; } .panel-cards { grid-template-columns: 1fr; } }
</style>
